<template>
  <v-app :class="{ blurred: this.GET_IS_BLURRED }">
    <v-app-bar color="white" app>
      <div class="flex align-center relative">
        <span v-if="!isApp">Menu</span>
        <v-app-bar-nav-icon id="sidebar-nav-button" @click.stop="drawer = !drawer" />
      </div>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      width="300"
      :class="{ 'drawer-loading': GET_IS_LOGGING_IN }"
      app
    >
      <sidebar />
    </v-navigation-drawer>

    <terms-of-use-dialog :show="canShowTerms" :on-cancel="() => isShowingTerms = false" :closeable="false"/>
    <survey-dialog :show="canShowSurvey" :on-cancel="() => isShowingSurvey = false" :closeable="false"/>

    <v-dialog v-model="isShowingDataRequestDialog" width="500">
      <v-card>
        <v-card-title>Nova solicitação de compartilhamento de dados</v-card-title>

        <div class="flex justify-end">
          <v-btn
            color="grey"
            @click="isShowingDataRequestDialog = false"
            text
          >
            Fechar
          </v-btn>

          <v-btn
            color="info"
            @click="goToSettings"
            text
          >
            Ver
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-main>
      <v-container :class="layoutProps.fullHeight ? 'h-full' : undefined">
        <slot />
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { IS_APP, IS_DEV } from '@/constants';
import ViewportMixin from '@/mixins/ViewportMixin.js'
import Sidebar from '@/components/menu/Sidebar';
import SurveyDialog from '@/components/SurveyDialog';
import TermsOfUseDialog from '@/components/TermsOfUseDialog';

const UNREAD_KEYS = ['helpdesk-admin', 'doctor', 'owner', 'helpdesk']

export default {
  name: 'SidebarLayout',

  mixins: [
    ViewportMixin,
  ],

  components: {
    Sidebar,
    SurveyDialog,
    TermsOfUseDialog,
  },

  data() {
    return {
      isApp: IS_APP,

      dashboard: null,
      chatsUnsubscribe: null,
      adminChatsUnsubscribe: null,

      chatUnreadCountForSection: {},

      isShowingTerms: false,
      isShowingSurvey: false,
      isShowingDataRequestDialog: false,

      // NOTE: Using null as the starting value for its v-model will initialize the drawer as closed on mobile and as open on desktop
      drawer: null,

      surveyTimer: null,
    }
  },

  created() {
    this.$store.dispatch('admin/subscriberAll');
    this.$store.dispatch('space/subscriberAll', {
      saasId: this.isSaasTeam ? this.readOnly?.saasId : undefined,
      ownerId: this.GET_USER_UID,
    });
    this.$store.dispatch('receiver/subscriberAll');
    this.$store.dispatch('user/subscribeAll');
    this.$store.dispatch('surveys/subscribePending')
      .then(() => {
        this.observeSurveys();
      });
    this.$store.dispatch('user/subscribePendingDataRequest');
    this.$store.dispatch('user/getValidatedSpecialtizations');
  },

  async mounted() {
    this.dashboard = (this.isApp || this.preferences?.useDoctorDashboard)
      ? 'doctor'
      : 'owner';

    this.refreshUnreadCount();
  },

  computed: {
    ...mapGetters('app', {
      appConfig: 'GET_CONFIG',
    }),
    ...mapGetters('user', {
      isLogged: 'IS_CONNECTED_AND_VERIFIED',
      preferences: 'GET_PREFERENCES',
      readOnly: 'GET_READONLY_INFO',
      userInfo: 'GET_USER_INFO',
      isCalendarEnabled: 'GET_CALENDAR_ENABLED',
      subscription: 'GET_SUBSCRIPTION',
    }),
    ...mapGetters('user', [ 'GET_IS_LOGGING_IN', 'GET_USER_UID', 'GET_UNREAD_COUNTS_PER_KEY', 'GET_IS_UPDATING', 'GET_HAS_PENDING_DATA_REQUESTS' ]),
    ...mapGetters('validateUser', [ 'GET_OWN_DOCTOR_VALIDATION' ]),
    ...mapGetters('admin', {
        adminUnsubscribers: 'GET_UNSUBSCRIBERS',
        pendingContentProductions: 'GET_ALL_PENDING_CONTENT_PRODUCTION',
      }),
    ...mapGetters('app', [ 'GET_IS_BLURRED' ]),
    ...mapGetters('subscription', {
      saasSubscription: 'GET_SAAS_SUBSCRIPTION',
    }),
    ...mapGetters('termsOfUse', { termsAccepted: 'ACCEPTED' }),
    ...mapGetters('team', {
      context: 'GET_CONTEXT',
      teamId: 'GET_TEAM_ID',
      teamRole: 'GET_ROLE',
      teamsCount: 'GET_TEAMS_COUNT',
    }),

    layoutProps () {
      return (this.$route.meta?.layoutProps ? this.$route.meta.layoutProps : {})
    },

    isSuperAdmin() {
      return this.readOnly?.isSuperAdmin;
    },

    isAdminOrSuperAdmin() {
      return this.readOnly?.isAdmin || this.readOnly?.isSuperAdmin;
    },

    isTeamEnabled() {
      return this.readOnly?.teamEnabled;
    },

    isDoctorSubscriptionEnabled() {
      return this.readOnly?.doctorSubscriptionEnabled;
    },

    canShowSaasItems() {
      return this.hasSaasSubscription
          && this.readOnly?.ownerSaasId
          || this.readOnly?.saasRole
          && this.readOnly?.saasRole !== 'user';
    },

    canShowSaasSubscriptionItem() {
      return !this.isSaas || this.readOnly?.ownerSaasId?.trim() || this.readOnly?.saasRole === 'owner';
    },

    isDoctorValidated() {
      return this.readOnly?.isDoctorValidated;
    },

    isDoctorDashboard() {
      if (this.isApp || this.isTeamMember) {
        return true;
      }

      if (this.isSaas) {
        return !this.isSaasTeam;
      }

      return this.canToggleView && this.dashboard === 'doctor';
    },

    isDashboardUpdated() {
      if (!this.preferences) {
        return false;
      }

      return this.userInfo?.preferences?.useDoctorDashboard
        ? this.dashboard !== 'doctor'
        : this.dashboard !== 'owner';
    },

    pendingOwnDoctorValidation() {
      return this.GET_OWN_DOCTOR_VALIDATION && !this.GET_OWN_DOCTOR_VALIDATION.isRead;
    },

    doctorValidationClass() {
      return this.isDoctorValidated ?
        'deep-purple lighten-5 deep-purple--text text--accent-4' :
        'grey lighten-3  grey--text';
    },

    impersonatingAdmin() {
      return this.isHelpDesk && this.selfAsHabitat && this.isAdminOrSuperAdmin
    },

    myId() {
      // TODO: enable secretary impersonation
      return this.impersonatingAdmin ? '__habitat_help' : this.GET_USER_UID;
    },

    usermail() {
      return this.userInfo?.email ?? '';
    },

    hasDocument() {
      return this.userInfo?.socialSecurity || this.userInfo?.cnpj;
    },

    mustAcceptTerms() {
      return this.termsAccepted === false;
    },

    routeTitle() {
      return this.$router.currentRoute?.meta?.title;
    },

    routeBack() {
      return this.$router.currentRoute?.meta?.backTo;
    },

    canShowTerms() {
      return this.isLogged
          && this.isShowingTerms
          && !this.readOnly?.mustSetPassword;
    },

    canShowSurvey() {
      return this.isLogged
          && this.isShowingSurvey
          && !this.isShowingTerms
          && !this.readOnly?.mustSetPassword;
    },

    logoUrl() {
      if (this.isSaas) {
        return this.appConfig?.saasLogo;
      }

      return '/icon.webp';
    },

    saasId() {
      return this.appConfig?.saasId || this.readOnly?.ownerSaasId;
    },

    isSaas() {
      return !!this.readOnly?.saasId;
    },

    isSaasTeam() {
      return this.isSaas && this.readOnly?.saasRole !== 'user';
    },

    isTeamManager() {
      return this.teamRole && this.teamRole !== 'user';
    },

    isTeamUser() {
      return this.teamRole === 'user';
    },

    isTeamMember() {
      return this.teamRole && this.teamRole !== 'owner';
    },

    isTeamOwner() {
      return this.teamRole === 'owner';
    },

    hasSaasSubscription() {
      return !!this.saasSubscription;
    },

    hasDataRequest() {
      return this.$router.currentRoute?.name !== 'settings-data-share-list' && this.GET_HAS_PENDING_DATA_REQUESTS;
    },
  },

  methods: {
    ...mapActions('user', ['signout']),

    refreshUnreadCount() {
      for (const k of UNREAD_KEYS) {
        this.$set(this.chatUnreadCountForSection, k, 0)
        if (k in this.GET_UNREAD_COUNTS_PER_KEY) {
          this.$set(this.chatUnreadCountForSection, k, Object.values(this.GET_UNREAD_COUNTS_PER_KEY[k]).reduce((a, b) => a + b, 0))
        }
      }
    },

    goToHome() {
      if (this.$router.currentRoute && this.$router.currentRoute.name !== 'home') {
        this.$router.push({ name: 'home' })
      } else {
        this.$router.go()
      }
    },

    async goToSettings() {
      if (this.$router.currentRoute?.name === 'settings-data-share-list') {
        return;
      }

      this.dashboard = 'doctor';
      this.$router.push({ name: 'settings-data-share-list' });
      this.isShowingDataRequestDialog = false;
    },

    async updateDashboardChoice(save) {
      await this.$store.dispatch('user/updatePreferences', {
        payload: {
          useDoctorDashboard: this.dashboard === 'doctor',
        },
        save,
      });
    },

    observeSurveys() {
      if (this.surveyTimer) {
        clearInterval(this.surveyTimer);
      }

      const checkSurvey = async () => {
        if (this.isShowingSurvey) {
          return;
        }

        const survey = await this.$store.dispatch('surveys/checkSurveys');
        this.isShowingSurvey = !!survey;
      };

      this.surveyTimer = setInterval(checkSurvey, 1_000 * (IS_DEV ? 15 : 30));
      checkSurvey();
    },

    prettySubscriptionStatus(status) {
      switch(status) {
        case 'active':
          return 'Ativa';
        case 'overdue':
          return 'Atrasada';
        case 'inactive':
          return 'Inativa';
        default: return null;
      }
    },

    prettySubscriptionStatusColor(status) {
      switch(status) {
        case 'active':
          return 'success';
        case 'overdue':
          return 'error';
        case 'inactive':
          return 'gray';
        default: return null;
      }
    },
  },

  watch: {
    GET_UNREAD_COUNTS_PER_KEY: {
      handler() {
        this.refreshUnreadCount()
      },
      deep: true,
    },

    dashboard: {
      handler() {
        this.updateDashboardChoice(false);
      },
    },

    termsAccepted: {
      handler() {
        this.isShowingTerms = this.termsAccepted === false;
      },
      immediate: true,
    },

    hasDataRequest(value) {
      this.isShowingDataRequestDialog = value;
    }
  },

  beforeDestroy() {
    if (this.chatsUnsubscribe) {
      this.chatsUnsubscribe()
    }
    if (this.adminChatsUnsubscribe) {
      this.adminChatsUnsubscribe()
    }

    if (this.surveyTimer) {
      clearInterval(this.surveyTimer);
    }

    this.$store.dispatch('admin/unsubscribeAll');
  },
}
</script>

<style lang="scss" scoped>
.v-toolbar__title {
  overflow: visible;
  display: flex;
}

.doctor-owner-switch {
  cursor: pointer;
  margin-right: -12px;
  margin-left: 4px;
}

::v-deep .doctor-owner-switch .v-input--switch__track {
  background-color: #7e57c2 !important;
}

::v-deep .doctor-owner-switch .v-input--switch__thumb {
  background-color: white !important;
}

.doctor-owner-container {
  flex: 0;
  padding: 8px 12px;

  p {
    color: gray;
  }

  .doctor-owner-switch {
    ::v-deep .v-input__slot {
      margin: 0;
    }

    ::v-deep .v-messages {
      min-height: 0px;
    }
  }

  .owner {
    margin-right: 0.1rem;
  }

  .doctor {
    margin-left: -1rem;
  }

  .active {
    font-weight: bold;
  }
}

.blurred {
  filter: blur(3px);
  pointer-events: none;
}

.doctor-validation {
  .doctor-validation-content {
    background-color: #eee !important;
  }

  &.selected .doctor-validation-content {
    background-color: #6200ea1e !important;
    color: #6200ea !important;
  }
}

.doctor-validation.validated:not(.selected) {
  .doctor-validation-content {
    color: #598000 !important;
    background-color: #91b50945 !important;
  }

  .v-icon {
    color: #598000 !important;
  }
}

::v-deep .drawer-loading .v-navigation-drawer__content {
  display: flex;
}

.last-menu {
  padding-bottom: 96px;
}
</style>

<style lang="scss" scoped>
.v-application--wrap {
  margin-top: env(safe-area-inset-top);
}

.v-app-bar {
  margin-top: env(safe-area-inset-top) !important;
}
.v-navigation-drawer__content {
  padding-top: env(safe-area-inset-top);

  [role="separator"] + [role="separator"] {
    visibility: hidden;
  }
}
</style>